import { useEffect, useId } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { v4 as uuid } from 'uuid'
import { supabase, type PlannedMeal } from '../lib/supabase'
import { isPlusRequiredError } from '../lib/billing'
import { addDays, clampServings, dayKey, fromKey } from '../lib/mealPlan'
import { revertRows, snapshotRows, type RowSnapshot } from '../lib/rows'
import { useIsVisible } from './useIsVisible'

const MEALS_KEY = (householdId: string, monday: string) => ['planned-meals', householdId, monday]

const byCreated = (a: PlannedMeal, b: PlannedMeal) => a.created_at.localeCompare(b.created_at)

type Patch = Partial<Pick<PlannedMeal, 'servings' | 'added' | 'cooked'>>

/**
 * One week of the household's meal plan (033 planned_meals), Monday `monday`
 * through Sunday. Every edit is optimistic and rolls back on error (a 42501 —
 * planning is Plus and the household is on Free — also calls onPlusRequired); another phone's
 * changes arrive over Realtime.
 */
export function useMealPlan(householdId: string, memberId: string, monday: string, options: { onPlusRequired?: () => void } = {}) {
  const qc = useQueryClient()
  const isVisible = useIsVisible()
  // Its own topic: two users of the same week (the tab and its count) would
  // otherwise get the same, already-subscribed channel back from supabase-js.
  const instance = useId()
  const key = MEALS_KEY(householdId, monday)
  const sunday = dayKey(addDays(fromKey(monday), 6))
  const { onPlusRequired } = options

  const query = useQuery({
    queryKey: key,
    enabled: !!householdId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('planned_meals').select('*')
        .eq('household_id', householdId)
        .gte('day', monday)
        .lte('day', sunday)
        .order('created_at')
      if (error) throw error
      return data as PlannedMeal[]
    },
  })

  useEffect(() => {
    if (!isVisible || !householdId) return
    const channel = supabase
      .channel(`planned_meals:${householdId}:${monday}:${instance}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'planned_meals', filter: `household_id=eq.${householdId}` },
        (payload) => {
          qc.setQueryData<PlannedMeal[]>(MEALS_KEY(householdId, monday), (old = []) => {
            if (payload.eventType === 'DELETE') return old.filter(m => m.id !== (payload.old as PlannedMeal).id)
            const row = payload.new as PlannedMeal
            const rest = old.filter(m => m.id !== row.id)
            return row.day >= monday && row.day <= sunday ? [...rest, row].sort(byCreated) : rest
          })
        })
      // Joined, or joined again after the tab was hidden: read what realtime missed.
      .subscribe(status => {
        if (status === 'SUBSCRIBED') void qc.invalidateQueries({ queryKey: MEALS_KEY(householdId, monday) })
      })
    return () => { supabase.removeChannel(channel) }
  }, [householdId, monday, sunday, qc, isVisible, instance])

  // A refused write (Plus needed, 42501) takes back the meals it touched, and
  // only those: a partner's changes that arrived meanwhile stay (lib/rows.ts).
  function optimistic<V>(write: (vars: V) => PromiseLike<{ error: unknown }>, apply: (old: PlannedMeal[], vars: V) => PlannedMeal[], idsOf: (vars: V) => string[]) {
    return {
      mutationFn: async (vars: V) => {
        const { error } = await write(vars)
        if (error) throw error
      },
      onMutate: async (vars: V) => {
        await qc.cancelQueries({ queryKey: key })
        const snaps = snapshotRows(qc.getQueryData<PlannedMeal[]>(key) ?? [], idsOf(vars))
        qc.setQueryData<PlannedMeal[]>(key, old => apply(old ?? [], vars))
        return { snaps }
      },
      onError: (err: unknown, _vars: V, ctx: { snaps?: Map<string, RowSnapshot<PlannedMeal>> } | undefined) => {
        if (ctx?.snaps) qc.setQueryData<PlannedMeal[]>(key, old => revertRows(old ?? [], ctx.snaps!))
        if (isPlusRequiredError(err)) onPlusRequired?.()
      },
    }
  }

  const insert = useMutation(optimistic<PlannedMeal>(
    row => supabase.from('planned_meals').insert({
      id: row.id, household_id: row.household_id, day: row.day, recipe_id: row.recipe_id,
      servings: row.servings, created_by: row.created_by,
    }),
    (old, row) => [...old, row],
    row => [row.id],
  ))
  const update = useMutation(optimistic<{ ids: string[]; patch: Patch }>(
    ({ ids, patch }) => supabase.from('planned_meals').update(patch).in('id', ids),
    (old, { ids, patch }) => old.map(m => (ids.includes(m.id) ? { ...m, ...patch } : m)),
    ({ ids }) => ids,
  ))
  const remove = useMutation(optimistic<string>(
    id => supabase.from('planned_meals').delete().eq('id', id),
    (old, id) => old.filter(m => m.id !== id),
    id => [id],
  ))

  return {
    meals: query.data ?? [],
    isLoading: query.isLoading,
    /** Plans a recipe on a day; returns the new meal's id. */
    addMeal: (day: string, recipeId: string, servings: number): string => {
      const now = new Date().toISOString()
      const row: PlannedMeal = {
        id: uuid(), household_id: householdId, day, recipe_id: recipeId, servings: clampServings(servings),
        added: false, cooked: false, created_by: memberId || null, created_at: now, updated_at: now,
      }
      insert.mutate(row)
      return row.id
    },
    removeMeal: (id: string) => remove.mutate(id),
    setServings: (id: string, servings: number) => update.mutate({ ids: [id], patch: { servings: clampServings(servings) } }),
    markCooked: (id: string, cooked = true) => update.mutate({ ids: [id], patch: { cooked } }),
    markAdded: (ids: string[], added: boolean) => { if (ids.length) update.mutate({ ids, patch: { added } }) },
  }
}
